import { ChevronLeftIcon, ChevronRightIcon } from '../Icons'
import { Text } from '../Text'

export interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  hasNext?: boolean
  hasPrevious?: boolean
  totalItems?: number
  itemsPerPage?: number
}

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  hasNext,
  hasPrevious,
  totalItems,
  itemsPerPage = 10,
}: PaginationProps) {
  const startItem = (currentPage - 1) * itemsPerPage + 1
  const endItem = Math.min(currentPage * itemsPerPage, totalItems || 0)

  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = []

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      pages.push(1)
      if (currentPage > 3) {
        pages.push('ellipsis')
      }
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i)
      }
      if (currentPage < totalPages - 2) {
        pages.push('ellipsis')
      }
      pages.push(totalPages)
    }

    return pages
  }

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-background rounded-b-lg">
      <Text as="div" size="body-small" color="subdued" spacing="tight">
        {totalItems ? (
          <>
            Exibindo <span className="font-[550]">{startItem}</span> a <span className="font-[550]">{endItem}</span> de{' '}
            <span className="font-[550]">{totalItems}</span> resultados
          </>
        ) : (
          `Página ${currentPage} de ${totalPages}`
        )}
      </Text>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={!hasPrevious && currentPage === 1}
          className="p-2 rounded-md border border-border hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background transition-colors"
          aria-label="Previous page"
        >
          <ChevronLeftIcon className="w-4 h-4 text-muted-foreground" />
        </button>
        {getPageNumbers().map((page, index) =>
          page === 'ellipsis' ? (
            <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                page === currentPage
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-border hover:bg-accent'
              }`}
            >
              {page}
            </button>
          )
        )}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNext && currentPage === totalPages}
          className="p-2 rounded-md border border-border hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background transition-colors"
          aria-label="Next page"
        >
          <ChevronRightIcon className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  )
}
